import { useMemo, useState, type ReactElement } from 'react';
import type { PostDto } from '@gr/core/content/posts/dto';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { Link, useNavigate, useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { BareSelect } from '../../../ui-kit/Field/Field';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { SplitLayout } from '../../../ui-kit/SplitLayout/SplitLayout';
import { useReferenceSearch } from '../entities';
import { PublicationHistoryPanel } from '../../distribution/publishing';
import { PostPlanningPanel } from '../../distribution/scheduling';
import { useBlog } from '../../accounts/workspaces';
import { draftOf, strictestCaptionLimit, updateRequestOf } from './model/post-draft';
import { PostEditor } from './PostEditor/PostEditor';
import { PostMediaPanel } from './PostMediaPanel';
import { ReadinessPanel } from './ReadinessPanel';
import { VariantsPanel } from './VariantsPanel';

// POST-UC-2, POST-UC-3, POST-UC-4, POST-UC-5, POST-UC-7, POST-UC-8 (+ SCH и PUB панели)
export function PostPage({ postId }: { postId: string }): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog, can } = useBlog();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const editable = can(BlogPermission.EditContent);
  const post = useResource(() => api.posts.get(blog.id, postId), [api, blog.id, postId]);
  const statuses = useResource(() => api.posts.statuses(blog.id), [api, blog.id]);
  const fields = useResource(() => api.entities.postFields(blog.id), [api, blog.id]);
  const formats = useResource(() => api.posts.formats(blog.id), [api, blog.id]);
  const channels = useResource(() => api.channels.list(blog.id), [api, blog.id]);
  const references = useReferenceSearch(blog.id, post.data?.referenceTitles);
  // Публикации поста — один запрос на «План» и «Историю публикаций». Правка текста и медиа их не
  // меняет; перечитываются при смене статуса (снятие с плана) и после действий в этих панелях.
  const publications = useResource(() => api.publishing.history(blog.id, postId), [api, blog.id, postId]);
  // Номер правки поста — готовность к каналам считается по тексту и медиа и перечитывается.
  const [revision, setRevision] = useState(0);
  const setStatus = useAction((statusId: string) => api.posts.setStatus(blog.id, postId, statusId));
  const remove = useAction(() => api.posts.delete(blog.id, postId));
  const captionLimit = useMemo(() => strictestCaptionLimit(channels.data ?? []), [channels.data]);
  // Черновик собирается один раз на загрузку поста: правки формы не сбрасываются чужими обновлениями.
  const draft = useMemo(() => (post.data ? draftOf(post.data) : null), [post.data?.id, fields.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPost = (updated: PostDto): void => {
    post.setData(updated);
    setRevision((r) => r + 1);
  };

  // План или выход изменились: публикации и пост (статус мог стать «Опубликован»).
  const publicationsChanged = async (): Promise<void> => {
    await Promise.all([publications.reload(), post.reload()]);
  };

  if ((post.loading && !post.data) || fields.loading) return <Loading label={t('common.loading')} />;
  if (post.error || !post.data || !draft) return <Alert tone="danger">{te(post.error)}</Alert>;
  const current = post.data;

  const onDelete = async (): Promise<void> => {
    if (!(await confirm({ title: t('posts.editor.deleteConfirm', { title: current.title }), confirmLabel: t('common.delete'), danger: true }))) return;
    if ((await remove.run()).success) navigate('posts', { blogId: blog.id });
  };

  return (
    <div className="u-stack-lg">
      <PageHeader
        title={current.title}
        subtitle={<Link to="posts" params={{ blogId: blog.id }}>{t('posts.editor.back')}</Link>}
        actions={
          <>
            <BareSelect
              label={t('posts.editor.status')}
              value={current.statusId}
              disabled={!editable || setStatus.pending}
              options={(statuses.data ?? []).map((s) => ({ value: s.id, label: `${s.name} · ${t(`posts.meta.${s.meta}`)}` }))}
              onChange={async (e) => {
                const result = await setStatus.run(e.target.value);
                if (result.success) {
                  applyPost(result.data);
                  void publications.reload();
                }
              }}
            />
            {editable ? (
              <Button variant="ghost" icon="trash" loading={remove.pending} onClick={() => void onDelete()}>
                {t('common.delete')}
              </Button>
            ) : null}
          </>
        }
      />
      {setStatus.error ? <Alert tone="danger">{te(setStatus.error)}</Alert> : null}
      {remove.error ? <Alert tone="danger">{te(remove.error)}</Alert> : null}
      <SplitLayout>
        <>
          <Card>
            <PostEditor
              draft={draft}
              fields={fields.data ?? []}
              formats={formats.data ?? []}
              references={references}
              captionLimit={captionLimit}
              editable={editable}
              onSubmit={async (values) => {
                const result = await api.posts.update(blog.id, postId, updateRequestOf(values));
                if (result.success) applyPost(result.data);
                return { ...result, saved: result.success ? draftOf(result.data) : null };
              }}
            />
          </Card>
          <VariantsPanel post={current} channels={channels.data ?? []} editable={editable} onChanged={applyPost} />
          <ReadinessPanel postId={postId} revision={revision} />
        </>
        <>
          <PostMediaPanel post={current} editable={editable} onChanged={applyPost} />
          <PostPlanningPanel postId={postId} meta={current.meta} publications={publications.data ?? []} onChanged={publicationsChanged} />
          <PublicationHistoryPanel postId={postId} channels={channels.data ?? []} publications={publications.data ?? []} onChanged={publicationsChanged} />
        </>
      </SplitLayout>
    </div>
  );
}
